// web/src/admin/AdminRuns.tsx — /admin/runs (portal spec 22): every update run (monthly + request, spec 20), filtered by
// status and reason, with Release / Retry (held monthly runs) / Cancel; a Spend tab (spec 20's update-spend: month picker,
// fleet total, runs, and per-site runs / total / average / max, sorted by total); and a Due tab, the Run-now list for U2's
// manual phase (spec 20's update-due: who the dispatcher would start today while UPDATE_DISPATCH_ENABLED is off).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { UpdateRun, UpdateRunStatus } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Skeleton } from '../components/Skeleton';
import { Btn, Empty, ago, fmtDate, inputCls, useAction } from './adminUi';

const STATUSES: UpdateRunStatus[] = ['queued', 'running', 'review', 'awaiting_approval', 'finalizing', 'publishing', 'reverting', 'live', 'held', 'failed', 'skipped'];
const OPEN: UpdateRunStatus[] = ['queued', 'running', 'review', 'awaiting_approval', 'finalizing', 'publishing', 'reverting'];
export const RUN_TONE: Record<UpdateRunStatus, string> = {
  queued: 'text-muted', running: 'text-info', review: 'text-run', awaiting_approval: 'text-run', finalizing: 'text-info', publishing: 'text-info',
  reverting: 'text-warn', live: 'text-success', held: 'text-danger', failed: 'text-danger', skipped: 'text-muted',
};
const thisMonth = () => new Date().toISOString().slice(0, 7);

export function AdminRuns() {
  const [tab, setTab] = useState<'runs' | 'spend' | 'due'>('runs');
  return (
    <>
      <PageHeader title="Monthly runs" subtitle="Every update run (monthly and client requests), what they cost, and who is due." />
      <div role="tablist" aria-label="Monthly runs" className="mb-4 flex flex-wrap gap-2">
        {([['runs', 'Runs'], ['spend', 'Spend'], ['due', 'Due today']] as const).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`min-h-11 rounded-full border px-4 text-sm ${tab === k ? 'border-run bg-run/10 text-ink' : 'border-hair text-muted'}`}>{label}</button>
        ))}
      </div>
      {tab === 'runs' && <Runs />}
      {tab === 'spend' && <Spend />}
      {tab === 'due' && <Due />}
    </>
  );
}

function Runs() {
  const q = useQuery({ queryKey: ['admin-runs'], queryFn: () => api.adminUpdateRuns(200) });
  const [status, setStatus] = useState<'' | UpdateRunStatus>('');
  const [reason, setReason] = useState<'' | 'monthly' | 'request'>('');
  const rows = (q.data ?? []).filter((r) => (!status || r.status === status) && (!reason || r.reason === reason));
  return (
    <>
      <div className="mb-3 grid grid-cols-2 gap-2 sm:max-w-md">
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className={inputCls}>
          <option value="">Any status</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value as typeof reason)} className={inputCls}>
          <option value="">Monthly + requests</option><option value="monthly">Monthly</option><option value="request">Requests</option>
        </select>
      </div>
      {q.isLoading && <Skeleton variant="row" count={4} />}
      {q.data && rows.length === 0 && <Empty>No runs{status || reason ? ' with these filters' : ' yet'}.</Empty>}
      <div className="flex min-w-0 flex-col gap-2">{rows.map((r) => <RunRow key={r.id} r={r} showSite />)}</div>
    </>
  );
}

/** One run with its admin actions (shared with the client page). */
export function RunRow({ r, showSite = false }: { r: UpdateRun; showSite?: boolean }) {
  const { busy, run } = useAction();
  const [confirm, setConfirm] = useState<'cancel' | 'release' | null>(null);
  const attempts = Object.entries(r.phaseAttempts ?? {}).filter(([, n]) => n > 0).map(([k, n]) => `${k} ×${n}`).join(', ');
  return (
    <div className="min-w-0 rounded-md border border-hair bg-surface p-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {showSite && r.slug && <Link to={`/admin/clients/${encodeURIComponent(r.slug)}`} className="inline-flex min-h-11 items-center break-words font-medium text-ink underline">{r.siteName ?? r.slug}</Link>}
          <div className="text-xs text-muted">{r.reason === 'request' ? 'Client request' : `Monthly ${r.period}`} · {r.phase}{attempts ? ` (${attempts})` : ''} · by {r.requestedBy} · {ago(r.createdAt)}</div>
        </div>
        <span className={`text-xs font-medium ${RUN_TONE[r.status]}`}>{r.status}{r.publishReadyAt && r.status === 'finalizing' ? ' · waiting its turn' : ''}</span>
      </div>
      <div className="mt-1 text-xs text-muted">${r.costUsd} of ${r.costCapUsd}{r.finishedAt ? ` · finished ${fmtDate(r.finishedAt, true)}` : ''}{r.mergeSha ? ` · ${r.mergeSha.slice(0, 7)}` : ''}</div>
      {r.message && <p className="mt-1 break-words text-xs text-ink">{r.message}</p>}
      {r.error && <p className="mt-1 break-words text-xs text-danger">{r.error}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {r.status === 'review' && r.reason === 'monthly' && <Btn kind="primary" disabled={!!busy} onClick={() => setConfirm('release')}>Release</Btn>}
        {r.status === 'held' && r.reason === 'monthly' && <Btn icon="refresh" disabled={!!busy} onClick={() => void run('retry', () => api.adminRetryRun(r.id), 'Run queued again.')}>Retry</Btn>}
        {OPEN.includes(r.status) && <Btn kind="danger" disabled={!!busy} onClick={() => setConfirm('cancel')}>Cancel</Btn>}
      </div>
      <ConfirmDialog open={confirm === 'release'} title="Release this month's update?" body="The client sees the report and the ideas now." confirmLabel="Release"
        onConfirm={() => { setConfirm(null); void run('release', () => api.adminReleaseRun(r.id), 'Released.'); }} onCancel={() => setConfirm(null)} />
      <ConfirmDialog open={confirm === 'cancel'} destructive title="Cancel this run?" body="It ends held. A monthly run gives back the publish lock it holds."
        confirmLabel="Cancel the run" onConfirm={() => { setConfirm(null); void run('cancel', () => api.adminCancelRun(r.id), 'Run canceled.'); }} onCancel={() => setConfirm(null)} />
    </div>
  );
}

function Spend() {
  const [month, setMonth] = useState(thisMonth());
  const q = useQuery({ queryKey: ['admin-spend', month], queryFn: () => api.adminUpdateSpend(month) });
  return (
    <>
      <label className="mb-3 flex max-w-xs flex-col gap-1 text-sm"><span className="text-muted">Month</span>
        <input type="month" value={month} max={thisMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} className={inputCls} /></label>
      {q.isLoading && <Skeleton variant="card" count={1} />}
      {q.data && (
        <>
          <div className="mb-3 grid grid-cols-3 gap-2">
            {[['Total', `$${q.data.totalUsd}`], ['Runs', String(q.data.runs)], ['Per site', `$${q.data.avgPerSiteUsd}`]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-hair bg-surface p-3"><div className="text-xs text-muted">{k}</div><div className="font-head text-lg text-ink">{v}</div></div>
            ))}
          </div>
          {q.data.sites.length === 0 ? <Empty>No runs in {month}.</Empty> : (
            <div className="min-w-0 overflow-x-auto rounded-lg border border-hair">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted"><tr>{['Site', 'Runs', 'Total', 'Average', 'Max'].map((h) => <th key={h} className="px-3 py-2 font-normal">{h}</th>)}</tr></thead>
                <tbody>
                  {q.data.sites.map((s) => (
                    <tr key={s.slug} className="border-t border-hair">
                      <td className="max-w-[9rem] break-words px-3 py-2"><Link className="text-ink underline" to={`/admin/clients/${encodeURIComponent(s.slug)}`}>{s.slug}</Link></td>
                      <td className="px-3 py-2 text-muted" title={`${s.monthlyRuns} monthly, ${s.requestRuns} requests`}>{s.runs}</td>
                      <td className="px-3 py-2 text-ink">${s.totalUsd}</td><td className="px-3 py-2 text-muted">${s.avgUsd}</td><td className="px-3 py-2 text-muted">${s.maxUsd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-2 text-xs text-muted">The same numbers reach you by email on the 1st (once sending is on).</p>
        </>
      )}
    </>
  );
}

function Due() {
  const q = useQuery({ queryKey: ['admin-due'], queryFn: () => api.adminUpdateDue() });
  const { busy, run } = useAction();
  const [post, setPost] = useState(true);
  return (
    <>
      {q.data && <p className="mb-3 text-sm text-muted">{q.data.dispatchEnabled ? 'The daily dispatcher is on: these start on their own.' : 'The daily dispatcher is off (manual first): start each one with Run now.'}</p>}
      <label className="mb-3 flex min-h-11 items-center gap-2 text-sm text-ink"><input type="checkbox" className="size-5" checked={post} onChange={(e) => setPost(e.target.checked)} />Post the month's report</label>
      {q.isLoading && <Skeleton variant="row" count={2} />}
      {q.data && q.data.items.length === 0 && <Empty>Nobody is due today.</Empty>}
      <div className="flex flex-col gap-2">
        {(q.data?.items ?? []).map((s) => (
          <div key={s.slug} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-hair bg-surface p-3 text-sm">
            <div className="min-w-0"><Link className="break-words text-ink underline" to={`/admin/clients/${encodeURIComponent(s.slug)}`}>{s.name}</Link>
              <div className="text-xs text-muted">due {fmtDate(s.nextRunAt, true)} · run day {s.runDay ?? '—'} · {s.kcReviewRunsLeft} reviews left</div></div>
            <Btn kind="primary" disabled={!!busy} onClick={() => void run(`run:${s.slug}`, () => api.adminRunUpdate(s.slug, post), 'Run queued.')}>Run now</Btn>
          </div>
        ))}
      </div>
    </>
  );
}
